import React, { useCallback, useEffect, useRef, useState } from 'react';
import './HistorialConciliacion.css';

const moneda = n => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(n || 0));
const entero = n => new Intl.NumberFormat('es-MX').format(Number(n || 0));
const fechaLocal = date => [date.getFullYear(), String(date.getMonth() + 1).padStart(2, '0'), String(date.getDate()).padStart(2, '0')].join('-');
const fechaHora = s => s ? new Date(s).toLocaleString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const ahora = new Date();
const filtrosIniciales = {
    Desde: fechaLocal(new Date(ahora.getFullYear(), ahora.getMonth(), 1)),
    Hasta: fechaLocal(ahora),
    Metodo: 'Todos', Estado: 'Todos', Buscar: ''
};

function crearParametros(f, pagina = 1, porPagina = 25) {
    const query = new URLSearchParams();
    Object.keys(f).forEach(k => { if (f[k] !== '' && f[k] != null) query.set(k, f[k]); });
    query.set('Pagina', String(pagina));
    query.set('PorPagina', String(porPagina));
    return query.toString();
}

async function solicitarJSON(url) {
    const respuesta = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (respuesta.status === 401 || respuesta.status === 403) {
        const e = new Error('La sesión del reporte no está vigente. Inicia sesión nuevamente.');
        e.sesion = true;
        throw e;
    }
    if (!respuesta.ok) {
        const error = await respuesta.json().catch(() => ({}));
        throw new Error(error.mensaje || 'No se pudo consultar el reporte.');
    }
    return respuesta.json();
}

async function bajarArchivo(url, nombreAlterno) {
    const respuesta = await fetch(url, { credentials: 'same-origin' });
    if (respuesta.status === 401 || respuesta.status === 403) {
        const error = new Error('Tu sesión expiró. Inicia sesión nuevamente.');
        error.sesion = true;
        throw error;
    }
    if (!respuesta.ok) {
        const error = await respuesta.json().catch(() => ({}));
        throw new Error(error.mensaje || 'El archivo no está disponible.');
    }
    const encabezado = respuesta.headers.get('content-disposition') || '';
    const archivo = encabezado.match(/filename\*=UTF-8''([^;]+)/i) || encabezado.match(/filename="?([^";]+)"?/i);
    const nombre = archivo ? decodeURIComponent(archivo[1]) : nombreAlterno;
    const blob = await respuesta.blob();
    const ubicacion = URL.createObjectURL(blob);
    const enlace = document.createElement('a');
    enlace.href = ubicacion;
    enlace.download = nombre;
    document.body.appendChild(enlace);
    enlace.click();
    enlace.remove();
    window.setTimeout(() => URL.revokeObjectURL(ubicacion), 2000);
}

function Icono({ nombre, size = 19 }) {
    const comun = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true };
    switch (nombre) {
        case 'tabla': return <svg {...comun}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M3 15h18M9 3v18"/></svg>;
        case 'reloj': return <svg {...comun}><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>;
        case 'descarga': return <svg {...comun}><path d="M12 3v12m-5-5 5 5 5-5M4 17v4h16v-4"/></svg>;
        case 'buscar': return <svg {...comun}><circle cx="10.7" cy="10.7" r="7"/><path d="m16 16 5 5"/></svg>;
        case 'refrescar': return <svg {...comun}><path d="M20 6v5h-5M4 18v-5h5"/><path d="M5.5 9a7 7 0 0 1 12.1-2L20 11M4 13l2.4 4A7 7 0 0 0 18.5 15"/></svg>;
        case 'escudo': return <svg {...comun}><path d="M12 2 4 5v6c0 5.2 3.4 8.6 8 11 4.6-2.4 8-5.8 8-11V5L12 2Z"/><path d="m9 12 2 2 4-4"/></svg>;
        case 'cheque': return <svg {...comun}><circle cx="12" cy="12" r="9"/><path d="m8 12 3 3 5-6"/></svg>;
        case 'alerta': return <svg {...comun}><path d="m12 3 10 18H2L12 3Z"/><path d="M12 9v4m0 4h.01"/></svg>;
        case 'cerrar': return <svg {...comun}><path d="M18 6 6 18M6 6l12 12"/></svg>;
        default: return <svg {...comun}><path d="M5 12h14m-6-6 6 6-6 6"/></svg>;
    }
}

const resumenVacio = { operaciones: 0, totalBruto: 0, cancelaciones: 0, totalNeto: 0, totalCerrado: 0, totalPendiente: 0, totalIva: 0, totalComisiones: 0, diferencias: 0, sinVinculoCorte: 0, sinDistribucion: 0 };

export default function HistorialConciliacion() {
    const [edicion, setEdicion] = useState({ ...filtrosIniciales });
    const [aplicados, setAplicados] = useState({ ...filtrosIniciales });
    const [pagina, setPagina] = useState(1);
    const [porPagina, setPorPagina] = useState(25);
    const [datos, setDatos] = useState(null);
    const [pestana, setPestana] = useState('movimientos');
    const [expandido, setExpandido] = useState(null);
    const [cargando, setCargando] = useState(false);
    const [exportando, setExportando] = useState(false);
    const [descargando, setDescargando] = useState(null);
    const [error, setError] = useState('');
    const [sinSesion, setSinSesion] = useState(false);
    const [segundos, setSegundos] = useState(0);
    const peticion = useRef(0);

    const consultar = useCallback(async (f, p, tamano) => {
        const actual = ++peticion.current;
        setCargando(true);
        setError('');
        try {
            const respuesta = await solicitarJSON('/api/ReporteConciliacion/Consultar?' + crearParametros(f, p, tamano));
            if (actual === peticion.current) {
                setDatos(respuesta);
                setExpandido(null);
                setSinSesion(false);
            }
        } catch (e) {
            if (actual === peticion.current) {
                setError(e.message);
                setSinSesion(!!e.sesion);
                if (e.sesion) setDatos(null);
            }
        } finally {
            if (actual === peticion.current) setCargando(false);
        }
    }, []);

    useEffect(() => {
        consultar(aplicados, pagina, porPagina);
    }, [consultar, aplicados, pagina, porPagina]);

    useEffect(() => {
        // No mostrar una pantalla vacía de importes $0 cuando la sesión caducó:
        // autenticar de nuevo en ESTA pestaña y regresar automáticamente al reporte.
        if (sinSesion) window.location.replace('/login_.html?returnUrl=%2Fhistorialpago');
    }, [sinSesion]);

    useEffect(() => {
        // Al volver de la pestaña principal se revalida la sesión y el usuario.
        const verificarAlVolver = () => consultar(aplicados, pagina, porPagina);
        window.addEventListener('focus', verificarAlVolver);
        return () => window.removeEventListener('focus', verificarAlVolver);
    }, [consultar, aplicados, pagina, porPagina]);

    useEffect(() => {
        if (!cargando && !exportando && descargando === null) return undefined;
        const inicio = Date.now();
        setSegundos(0);
        const intervalo = window.setInterval(() => setSegundos(Math.floor((Date.now() - inicio) / 1000)), 500);
        return () => window.clearInterval(intervalo);
    }, [cargando, exportando, descargando]);

    const aplicar = e => {
        if (e) e.preventDefault();
        setPagina(1);
        setAplicados({ ...edicion });
    };

    const limpiar = () => {
        setEdicion({ ...filtrosIniciales });
        setPagina(1);
        setAplicados({ ...filtrosIniciales });
    };

    const exportar = async () => {
        setExportando(true);
        setError('');
        try {
            await bajarArchivo('/api/ReporteConciliacion/Exportar?' + crearParametros(aplicados), 'SAIEC_Conciliacion.xlsx');
        } catch (e) {
            setError(e.message);
            if (e.sesion) { setSinSesion(true); setDatos(null); }
        } finally { setExportando(false); }
    };

    const descargarRecibo = async m => {
        setDescargando(m.id);
        setError('');
        try {
            await bajarArchivo('/api/ReporteConciliacion/Recibo/' + m.id, 'Recibo_' + m.folio + '.pdf');
        } catch (e) {
            setError(e.message);
            if (e.sesion) { setSinSesion(true); setDatos(null); }
        } finally { setDescargando(null); }
    };

    const resumen = datos?.resumen || resumenVacio;
    const registros = datos?.registros || [];
    const metodos = datos?.metodos || [];
    const totalPaginas = Math.max(1, Math.ceil((datos?.total || 0) / porPagina));
    const alertas = resumen.diferencias + resumen.sinVinculoCorte + resumen.sinDistribucion;

    const cambio = (clave, valor) => setEdicion(previo => ({ ...previo, [clave]: valor }));

    return (
        <main className="hc-page">
            <div className="hc-shell">
                <header className="hc-topbar">
                    <div className="hc-brand">
                        <div className="hc-brand-mark"><Icono nombre="tabla" size={22}/></div>
                        <div><div className="hc-brand-name">SAIEC <span>/ PORTAL</span></div><div className="hc-brand-desc">Ingresos y control escolar</div></div>
                    </div>
                    <div className="hc-topbar-right">
                        <span className="hc-security"><Icono nombre="escudo" size={16}/> Consulta personal protegida</span>
                        <button className="hc-close" onClick={() => window.close()} title="Cerrar la pestaña del reporte"><Icono nombre="cerrar" size={18}/> Cerrar reporte</button>
                    </div>
                </header>

                <div className="hc-heading">
                    <div>
                        <div className="hc-eyebrow"><span className="hc-bullet"/> TESORERÍA / REPORTES</div>
                        <h1>Historial <span>y conciliación</span> de pagos</h1>
                        <p>Revisa tus cobros, identifica movimientos pendientes de corte y descarga el detalle para conciliación.</p>
                    </div>
                    <button type="button" className="hc-export" onClick={exportar} disabled={exportando || cargando || sinSesion}>
                        <Icono nombre="descarga"/> {exportando ? `Generando Excel · ${segundos}s` : 'Descargar Excel'}
                    </button>
                </div>

                <section className="hc-filter-panel" aria-label="Filtros del reporte">
                    <form onSubmit={aplicar}>
                        <div className="hc-filters">
                            <label>Fecha desde<input type="date" value={edicion.Desde} onChange={e => cambio('Desde', e.target.value)} required /></label>
                            <label>Fecha hasta<input type="date" value={edicion.Hasta} onChange={e => cambio('Hasta', e.target.value)} required /></label>
                            <label>Método de pago<select value={edicion.Metodo} onChange={e => cambio('Metodo', e.target.value)}>
                                <option>Todos</option><option>Efectivo</option><option>Tarjeta débito</option><option>Tarjeta crédito</option><option>Transferencia electrónica</option><option>Cheque</option><option>IEC</option>
                            </select></label>
                            <label>Estado de corte<select value={edicion.Estado} onChange={e => cambio('Estado', e.target.value)}>
                                <option>Todos</option><option>Pendiente</option><option>Cerrado</option><option>Cancelado</option><option>Cancelación parcial</option>
                            </select></label>
                            <label className="hc-search-field">Folio, alumno o concepto<div className="hc-input-icon"><Icono nombre="buscar" size={17}/><input value={edicion.Buscar} onChange={e => cambio('Buscar', e.target.value)} placeholder="Buscar en tus cobros…" maxLength="100" /></div></label>
                        </div>
                        <div className="hc-filter-actions">
                            <span>Mostrando únicamente los pagos de tu usuario · {datos?.usuario || 'sesión protegida'}</span>
                            <div><button type="button" className="hc-link-button" onClick={limpiar}>Restablecer</button><button type="submit" className="hc-primary-button" disabled={cargando || sinSesion}><Icono nombre="refrescar" size={16}/>{cargando ? `Consultando · ${segundos}s` : 'Aplicar filtros'}</button></div>
                        </div>
                    </form>
                </section>

                {error && <div className="hc-alert" role="alert"><Icono nombre="alerta" size={18}/><div>{error}{sinSesion && <a href="/login_.html">Ir a inicio de sesión</a>}</div></div>}

                <section className="hc-kpis" aria-label="Resumen del periodo">
                    <div className="hc-kpi"><span>Importe neto del periodo</span><strong>{moneda(resumen.totalNeto)}</strong><small>Bruto {moneda(resumen.totalBruto)} · Cancelado {moneda(resumen.cancelaciones)}</small></div>
                    <div className="hc-kpi"><span>Pagos registrados</span><strong>{entero(resumen.operaciones)}</strong><small>Operaciones en el periodo filtrado</small></div>
                    <div className="hc-kpi hc-kpi-highlight"><span>Pendiente de cierre</span><strong>{moneda(resumen.totalPendiente)}</strong><small><Icono nombre="reloj" size={14}/> Por incluir o confirmar en corte</small></div>
                    <div className="hc-kpi"><span>IVA y comisiones</span><strong>{moneda(resumen.totalIva + resumen.totalComisiones)}</strong><small>IVA {moneda(resumen.totalIva)} · Comisión {moneda(resumen.totalComisiones)}</small></div>
                    <div className="hc-kpi"><span>Observaciones</span><strong>{entero(alertas)}</strong><small>Revisar vínculo de corte o distribución</small></div>
                </section>

                <section className="hc-report-panel">
                    <div className="hc-tabs" role="tablist" aria-label="Vistas del reporte">
                        <button className={pestana === 'movimientos' ? 'active' : ''} onClick={() => setPestana('movimientos')} role="tab" aria-selected={pestana === 'movimientos'}><Icono nombre="tabla" size={17}/> Movimientos</button>
                        <button className={pestana === 'metodos' ? 'active' : ''} onClick={() => setPestana('metodos')} role="tab" aria-selected={pestana === 'metodos'}>Por método de pago</button>
                        <button className={pestana === 'corte' ? 'active' : ''} onClick={() => setPestana('corte')} role="tab" aria-selected={pestana === 'corte'}>Conciliación del corte</button>
                        <span className="hc-tab-total">{entero(datos?.total || 0)} movimientos</span>
                    </div>

                    {pestana === 'movimientos' && <div className="hc-table-scroll"><table className="hc-table">
                        <thead><tr><th>Folio / fecha</th><th>Alumno</th><th>Ciclo escolar / concepto</th><th>Método</th><th className="hc-right">Total</th><th>Estado</th><th className="hc-center">Recibo</th></tr></thead>
                        <tbody>{registros.map(m => <React.Fragment key={m.id}>
                            <tr className="hc-row" onClick={() => setExpandido(expandido === m.id ? null : m.id)}>
                                <td><strong className="hc-folio">#{m.folio}</strong><small>{fechaHora(m.fecha)}</small></td>
                                <td><strong>{m.alumno}</strong><small>{m.nivel} {m.grado || ''} {m.grupo || ''} · ID {m.idAlumno}</small></td>
                                <td><strong>{m.cicloEscolar}</strong><small>{(m.conceptos || []).map(c => c.mes || c.concepto).filter(Boolean).slice(0, 2).join(', ') || 'Concepto no especificado'}{(m.conceptos || []).length > 2 ? '…' : ''}</small></td>
                                <td>{m.metodo}</td>
                                <td className="hc-right hc-amount">{moneda(m.neto)}{m.cancelado > 0 && <small>Cancelado {moneda(m.cancelado)}</small>}</td>
                                <td><span className={'hc-status ' + (m.estado === 'Cerrado' ? 'closed' : m.estado === 'Pendiente' ? 'pending' : 'cancelled')}><span/>{m.estado}</span></td>
                                <td className="hc-center"><button className="hc-receipt" disabled={!m.tieneRecibo || descargando === m.id} title="Descargar recibo PDF" onClick={e => { e.stopPropagation(); descargarRecibo(m); }}><Icono nombre="descarga" size={18}/>{descargando === m.id && <small>{segundos}s</small>}</button></td>
                            </tr>
                            {expandido === m.id && <tr className="hc-expanded"><td colSpan="7"><div className="hc-details"><div><h4>Conceptos del recibo</h4>{(m.conceptos || []).map((c, i) => <p key={i}>{c.concepto}{c.mes ? ' · ' + c.mes : ''} <strong>{moneda(c.importe)}</strong><small>Catálogo #{c.idCatalogoCobro} · Cant. {c.cantidad}</small></p>)}{(!m.conceptos || !m.conceptos.length) && <p>Sin conceptos registrados.</p>}</div><div><h4>Distribución del pago</h4>{(m.metodos || []).map((p, i) => <p key={i}>{p.metodo}<strong>{moneda(p.totalAsignado)}</strong><small>Base {moneda(p.importe)} · IVA {moneda(p.iva)} · Comisión {moneda(p.comision)}</small></p>)}{m.observacion && <div className="hc-observation">{m.observacion}</div>}</div></div></td></tr>}
                        </React.Fragment>)}{registros.length === 0 && <tr><td colSpan="7" className="hc-empty">No hay pagos para los filtros seleccionados.</td></tr>}</tbody>
                    </table></div>}

                    {pestana === 'metodos' && <div className="hc-table-scroll"><table className="hc-table"><thead><tr><th>Método de pago</th><th className="hc-right">Operaciones</th><th className="hc-right">Base</th><th className="hc-right">IVA</th><th className="hc-right">Comisiones</th><th className="hc-right">Total asignado</th></tr></thead><tbody>
                        {metodos.map(m => <tr key={m.metodo}><td><strong>{m.metodo}</strong></td><td className="hc-right">{entero(m.operaciones)}</td><td className="hc-right">{moneda(m.importe)}</td><td className="hc-right">{moneda(m.iva)}</td><td className="hc-right">{moneda(m.comision)}</td><td className="hc-right hc-amount">{moneda(m.total)}</td></tr>)}
                        {!metodos.length && <tr><td colSpan="6" className="hc-empty">Sin métodos de pago registrados.</td></tr>}
                    </tbody></table><p className="hc-note">Un recibo pagado con varios métodos aparece distribuido entre ellos, sin duplicar su total. Las cancelaciones parciales requieren revisión manual.</p></div>}

                    {pestana === 'corte' && <div className="hc-corte-content"><div className="hc-corte-overview"><div><span>Registrado y cerrado</span><strong>{moneda(resumen.totalCerrado)}</strong><small>Movimientos con corte cerrado</small></div><div><span>Pendiente por cerrar</span><strong>{moneda(resumen.totalPendiente)}</strong><small>Movimientos aún abiertos</small></div><div><span>Diferencias a verificar</span><strong>{entero(resumen.diferencias)}</strong><small>Métodos vs. importe del recibo</small></div></div><div className="hc-table-scroll"><table className="hc-table"><thead><tr><th>Folio</th><th>Alumno</th><th>Estado del corte</th><th className="hc-right">Total neto</th><th className="hc-right">Diferencia</th><th>Observaciones</th></tr></thead><tbody>
                        {registros.map(m => <tr key={m.id}><td><strong>#{m.folio}</strong><small>{fechaHora(m.fecha)}</small></td><td>{m.alumno}</td><td><span className={'hc-status ' + (m.estado === 'Cerrado' ? 'closed' : m.estado === 'Pendiente' ? 'pending' : 'cancelled')}><span/>{m.estado}</span></td><td className="hc-right hc-amount">{moneda(m.neto)}</td><td className={'hc-right ' + (Math.abs(m.diferenciaMetodos) > 0.01 ? 'hc-difference' : '')}>{moneda(m.diferenciaMetodos)}</td><td>{m.observacion || <span className="hc-ok"><Icono nombre="cheque" size={16}/> Sin observaciones</span>}</td></tr>)}
                        {!registros.length && <tr><td colSpan="6" className="hc-empty">Sin movimientos en el periodo.</td></tr>}
                    </tbody></table></div><p className="hc-note">El estado “Cerrado” refleja el cierre registrado en el sistema; no sustituye la conciliación bancaria ni certifica la recepción del dinero.</p></div>}

                    {pestana !== 'metodos' && <footer className="hc-pagination"><div>Filas <strong>{datos?.total ? (pagina - 1) * porPagina + 1 : 0}–{Math.min(pagina * porPagina, datos?.total || 0)}</strong> de <strong>{entero(datos?.total || 0)}</strong></div><div className="hc-page-controls"><label>Por página <select value={porPagina} onChange={e => { setPorPagina(Number(e.target.value)); setPagina(1); }}><option value={10}>10</option><option value={25}>25</option><option value={50}>50</option><option value={100}>100</option></select></label><button disabled={pagina <= 1 || cargando} onClick={() => setPagina(pagina - 1)} aria-label="Página anterior">‹</button><span>{pagina} / {totalPaginas}</span><button disabled={pagina >= totalPaginas || cargando} onClick={() => setPagina(pagina + 1)} aria-label="Página siguiente">›</button></div></footer>}
                </section>

                <footer className="hc-page-foot"><span>SAIEC · Reporte generado desde información registrada en caja</span><span><Icono nombre="escudo" size={15}/> Acceso restringido por usuario y sesión</span></footer>
            </div>
        </main>
    );
}
